import { ArrowLeft, Bomb, LayoutGrid, LifeBuoy, MessageSquare, Shield, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { openSupport, useSupportAvailable } from '@/lib/freshdesk';
import type { ClockState } from './ClockStrip';

/**
 * Prompt 9: the mobile draft room (1d).
 *
 * SAB-871: WIRED, and the switch is `useIsMobile()` -- ONE mechanism, at 1024px + touch,
 * with iPad forced inside that hook. The previous version of this comment said "under 900px
 * and forced on iPad via the existing `data-ipad` attribute", and both halves were wrong:
 * nothing rendered this file at all, and `data-ipad` is a CSS mechanism for the APP nav
 * (it rewrites `md:hidden` utilities in index.css), not a room switch. 900 was never a
 * measured constraint either -- see SAB-871 for why 1024 instead.
 *
 * ONE PANEL AT A TIME. Not a scrolling stack of the desktop panels -- the tab bar swaps the
 * panel, and only the panel scrolls. Structure top to bottom: clock header → panel toolbar →
 * scrolling panel → action bar → tab bar.
 *
 * ── 44px TARGETS AND SAFE AREAS ─────────────────────────────────────────────
 *
 * Every interactive target is >=44px. The tab bar and action bar use env(safe-area-inset-*)
 * so the primary draft button does not sit under the home indicator -- on this screen the
 * action bar IS the draft action, and losing its bottom 20px to the indicator makes the
 * primary verb of the page unreliable.
 *
 * Feed folds into the CHAT tab as a segmented control, keeping the bar at four items.
 */

export type MobileTab = 'players' | 'board' | 'myteam' | 'chat';

export interface MobileDraftRoomProps {
  timeLeft: number | null;
  clockState: ClockState;
  clockEyebrow: string;
  clockHeadline: string;
  clockSubline: string;

  /** Cards held, or null when unknown — see ClockStrip. */
  sabotageCount: number | null;
  onSabotageClick: () => void;

  tab: MobileTab;
  onTabChange: (t: MobileTab) => void;
  /**
   * SAB-931 flip gate, item 4: the commissioner's controls.
   *
   * A NODE, not a set of handlers — the caller passes the SAME <CommishMenu> it gives the
   * desktop room, so pause/resume, undo, cancel and auto-draft-all behave identically and
   * their confirmation dialogs are not reimplemented here. This room had none of them: a
   * commissioner on a phone, mid-draft, could not pause, undo or cancel anything.
   */
  commishMenu?: React.ReactNode;
  /**
   * SAB-959: the AUTODRAFT pill, as a NODE for the same reason as commishMenu -- the
   * caller passes the same <AutoDraftControl> the desktop toolbar gets, so the four-state
   * `null` handling and its persistence are shared rather than reimplemented here.
   */
  autoDraftPill?: React.ReactNode;
  unreadChat: number;

  /**
   * SAB-871: the way out. NOT optional.
   *
   * This room replaces the app's bottom nav, and below 640px that bar is the only tab
   * navigation there is (`NFLNav`'s top strip is `hidden sm:flex`). Suppress it with nothing
   * in its place and the user is genuinely stuck in the draft room. Required rather than
   * `onExit?:` so a caller cannot omit it and strand people by accident.
   */
  onExit: () => void;

  miniTicker: React.ReactNode;
  panelToolbar?: React.ReactNode;
  panel: React.ReactNode;

  /** Action bar. */
  isMyTurn: boolean;
  /**
   * SAB-885: the draft is OVER -- a third condition, not a variant of whose turn it is.
   *
   * Without it the bar read "Pick a player -- --:-- left", enabled, on a finished draft: the
   * caller passes `isMyTurn || isCommissioner`, and a commissioner satisfies that forever.
   * Coercing isMyTurn to false instead would only have swapped one false statement for
   * another -- "Queue is set - 0 picks away" is equally untrue once there is nothing coming.
   */
  draftComplete?: boolean;
  /** Top-of-queue player, if any. */
  topOfQueue?: { name: string; position: string } | null;
  picksAway?: number;
  onPrimaryAction: () => void;
  onQueueButton: () => void;
  drafting?: boolean;
}

function mmss(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '--:--';
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * SAB-959: the clock moved from a 56px tile into the status sub-line, and its STATE
 * colouring moved with it rather than being dropped.
 *
 * The tile carried urgency on three channels -- border, fill and `animate-pulse-glow`.
 * A sub-line has room for one, so colour does the work and `urgent` keeps the pulse.
 * Losing the signal entirely was the alternative, and a draft clock running out is
 * exactly the moment the room must not go quiet.
 */
const CLOCK_TEXT: Record<ClockState, string> = {
  running: 'text-primary',
  urgent: 'text-damage animate-pulse-glow',
  paused: 'text-gold',
  yours: 'text-primary',
  idle: 'text-muted-foreground',
};

const TABS: { id: MobileTab; label: string; Icon: typeof Users }[] = [
  { id: 'players', label: 'PLAYERS', Icon: Users },
  { id: 'board', label: 'BOARD', Icon: LayoutGrid },
  { id: 'myteam', label: 'MY TEAM', Icon: Shield },
  { id: 'chat', label: 'CHAT', Icon: MessageSquare },
];

export default function MobileDraftRoom({
  timeLeft, clockState, clockEyebrow, clockHeadline, clockSubline,
  sabotageCount, onSabotageClick,
  tab, onTabChange, unreadChat, onExit, commishMenu, autoDraftPill,
  miniTicker, panelToolbar, panel,
  isMyTurn, topOfQueue, picksAway, onPrimaryAction, onQueueButton, drafting, draftComplete,
}: MobileDraftRoomProps) {
  // A finished draft is checked FIRST, before either turn branch, because both of them
  // describe a draft that is still running.
  //
  // Not "Draft complete": the clock strip directly above already renders exactly that as its
  // headline, and its subline as `Draft complete · N picks`. Three of the same sentence
  // stacked reads as a rendering fault. This says what the BAR is for -- why it is offering
  // nothing -- which is the one thing the clock does not already say.
  const primaryLabel = draftComplete
    ? 'No picks remaining'
    : !isMyTurn
      ? `Queue is set · ${picksAway ?? 0} pick${picksAway === 1 ? '' : 's'} away`
      : topOfQueue
        ? `Draft ${topOfQueue.name}`
        : `Pick a player — ${mmss(timeLeft)} left`;
  const canAct = isMyTurn && !draftComplete;

  /**
   * SAB-943: the room's own support route. The floating launcher is hidden in here
   * (SAB-942) because it overlays the scrolling panel; this is what replaces it, and A does
   * not ship without it -- a draft is the one window where a user is on a clock, has money
   * in the league, and cannot navigate away to find help.
   */
  const supportAvailable = useSupportAvailable();

  return (
    <div
      data-testid="nfl-draft-room-mobile"
      /* SAB-884: h-full, not h-[100dvh] -- the CALLER owns the height, exactly as SAB-873
         established for the desktop room. Claiming a full viewport height while sitting
         inside a container that had already spent 64px on the app nav put this room at
         top 64 / bottom 908 against an 844 viewport: 64px past the bottom before the footer
         was even counted. The reasoning was written into NFLDraftRoom's header at the time
         and not carried across to this file. */
      className="h-full overflow-hidden flex flex-col bg-background text-foreground"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <header
        className={cn(
          'flex-none border-b border-border px-[16px] pt-[12px] pb-[10px]',
          clockState === 'paused' && 'bg-gradient-to-b from-gold/10 to-transparent',
          clockState === 'yours' && 'bg-gradient-to-b from-primary/10 to-transparent',
        )}
      >
        <div className="flex items-center gap-[10px]">
          {/* SAB-871: the exit. First thing in the header, 44px, and a real label -- the
              logo in the top bar goes to "/" and leaves the NFL shell entirely, which is not
              what "leave the draft" should mean. Deliberately NOT a fifth tab: the bar is
              four items by design. No confirmation, because leaving is only navigation --
              the draft continues server-side and autodraft covers picks. */}
          <button
            data-testid="mobile-exit"
            onClick={onExit}
            aria-label="Leave the draft room"
            className="h-[44px] w-[36px] flex-none rounded-[10px] flex items-center justify-center text-muted-foreground"
          >
            <ArrowLeft className="h-[20px] w-[20px]" />
          </button>

          {/* SAB-959: three nowrap lines. Every one truncates rather than wraps -- the
              header must stay a single row, and a wrapped title is what pushes the player
              list back down the screen. `min-w-0` is what lets truncate actually engage
              inside a flex row; without it the block refuses to shrink and the pill and
              bomb get pushed off the right edge instead. */}
          <div className="min-w-0 flex-1">
            <div
              data-testid="mobile-clock-eyebrow"
              className={cn(
                'truncate font-display text-[9.5px] font-extrabold uppercase tracking-[0.12em]',
                // The design gives the on-the-clock eyebrow the primary orange. Driven by
                // clockState rather than a string match on the text, so it cannot drift
                // away from the eyebrow's own wording.
                clockState === 'yours' ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              {clockEyebrow}
            </div>
            <div className="truncate font-display text-[19px] font-bold leading-[1.15] text-white">
              {clockHeadline}
            </div>
            <div className="mt-px truncate text-[11px] font-medium text-muted-foreground">
              {/* Keeps `mobile-clock-tile` even though the tile is gone: the id names WHERE
                  THE CLOCK IS, and e2e/specs/live-draft-room.spec.ts parses m:ss out of it
                  to assert the timer actually ticks. Renaming it would have made that spec
                  silently find nothing on a phone viewport and report a null clock. */}
              <span
                data-testid="mobile-clock-tile"
                data-state={clockState}
                className={cn('tabular-nums', CLOCK_TEXT[clockState])}
              >
                {mmss(timeLeft)}
              </span>
              {' \u00b7 '}
              {clockSubline}
            </div>
          </div>

          {/* SAB-959: the pill replaces the full-width AUTODRAFT card that used to sit
              under the header. */}
          {autoDraftPill}

          <button
            data-testid="mobile-sabotage"
            onClick={onSabotageClick}
            disabled={sabotageCount !== null && sabotageCount <= 0}
            aria-label="Sabotage"
            className={cn(
              'relative h-[44px] w-[44px] flex-none rounded-[12px] bg-[image:var(--gradient-damage)] flex items-center justify-center shadow-[0_0_20px_hsl(var(--primary)/0.35)]',
              sabotageCount !== null && sabotageCount <= 0 && 'opacity-40',
            )}
          >
            <Bomb className="h-[22px] w-[22px] text-white" />
            {sabotageCount !== null && sabotageCount > 0 && (
              <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-white px-1 font-display text-[9px] font-extrabold text-damage">
                {sabotageCount}
              </span>
            )}
          </button>

        </div>

        {/*
          SAB-959: the header ROW is exactly design 2c -- back, status, pill, bomb.

          Support and the commissioner menu share the ticker line instead. Measured, six
          items cannot fit 390px: 394px of content against a 358px row, and flex resolves
          that by collapsing the only flex-1 child, so the status block -- eyebrow,
          headline and clock, the reason the header exists -- rendered at width 0 and was
          invisible. Both controls stay inside the header element, un-tab-gated and one
          tap away; they are simply on the second line.

          Support is rendered for EVERYONE; CommishMenu returns null unless the viewer is
          the commissioner, so for an ordinary manager this line is support + ticker.
        */}
        <div className="mt-[8px] flex items-center gap-[10px]">
          {/* ABSENT, NOT INERT (SAB-790). Rendered only once the widget is genuinely
              loaded -- consent alone is not enough, because the vendor script can still be
              in flight and a control that does nothing when tapped is the exact pattern the
              dead commissioner buttons established. Still a SIBLING of the scrolling panel,
              so it cannot overlay board or roster content however the panel grows.

              Kept deliberately: 2c has no support entry, and dropping it would leave the
              draft room with no support route at all, which is the whole of SAB-943. */}
          {supportAvailable && (
            <button
              data-testid="mobile-support"
              onClick={() => openSupport()}
              aria-label="Contact support"
              className="h-[44px] w-[44px] flex-none rounded-[10px] flex items-center justify-center text-muted-foreground"
            >
              <LifeBuoy className="h-[20px] w-[20px]" />
            </button>
          )}

          {/* SAB-931 item 4. Renders nothing for a non-commissioner. */}
          {commishMenu && <div className="flex-none">{commishMenu}</div>}

          <div className="min-w-0 flex-1 overflow-x-auto">{miniTicker}</div>
        </div>
      </header>

      {/* SAB-959: px to match the header above it. The design's search row is 6px 16px
          10px; the rem utilities that were here rendered ~15.75px of side padding against
          the header's 16px, because the root font-size is 18px. */}
      {panelToolbar && (
        <div className="flex-none border-b border-border px-[16px] pt-[6px] pb-[10px]">{panelToolbar}</div>
      )}

      {/* The ONLY scroll region on this screen. */}
      <div data-testid="mobile-panel" className="flex-1 min-h-0 overflow-y-auto">{panel}</div>

      <div className="flex-none flex items-center gap-2 border-t border-border bg-card px-3.5 py-2.5">
        <Button
          data-testid="mobile-primary-action"
          data-draft-complete={draftComplete ? 'true' : undefined}
          disabled={!canAct || drafting}
          onClick={onPrimaryAction}
          className={cn(
            'h-12 flex-1 flex-col gap-0 rounded-[11px]',
            canAct
              ? 'bg-[image:var(--gradient-sabotage)] text-[#12130f] shadow-[var(--shadow-sabotage)]'
              : 'border border-border bg-muted text-[#c9cdd6]',
          )}
        >
          <span className="font-display text-sm font-extrabold leading-tight">{primaryLabel}</span>
          {canAct && topOfQueue && (
            <span className="font-display text-[9.5px] font-bold leading-tight text-black/55">
              TOP OF QUEUE · {topOfQueue.position}
            </span>
          )}
        </Button>
        <Button
          data-testid="mobile-queue-button"
          variant="outline"
          size="icon"
          aria-label="Open queue"
          onClick={onQueueButton}
          className="h-12 w-12 flex-none rounded-[11px]"
        >
          <Users className="h-5 w-5" />
        </Button>
      </div>

      <nav
        data-testid="mobile-tabbar"
        className="flex-none flex border-t border-border bg-background pt-1.5"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        {TABS.map(({ id, label, Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              data-testid={`mobile-tab-${id}`}
              aria-selected={active}
              role="tab"
              onClick={() => onTabChange(id)}
              className={cn(
                'relative flex min-h-11 flex-1 flex-col items-center justify-center gap-[3px]',
                active ? 'text-primary' : 'text-muted-foreground opacity-60',
              )}
            >
              <Icon className="h-4 w-4" />
              <span className="font-display text-[9.5px] font-bold">{label}</span>
              {id === 'chat' && unreadChat > 0 && (
                <span className="absolute right-[22%] top-0 min-w-[15px] rounded-full bg-damage px-1 text-center font-display text-[9px] font-extrabold text-white">
                  {unreadChat}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
